import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  IconButton,
  Slider,
  Stack,
  CircularProgress,
} from "@mui/material";
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Fullscreen,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { videosAPI } from "../../services/api";
import { useTranslation } from "react-i18next";
import CustomContainer from "../../components/common/CustomContainer";

const VideoContainer = styled(Paper)(({ theme }) => ({
  position: "relative",
  backgroundColor: "#000",
  aspectRatio: "16/9",
  maxHeight: "80vh",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
}));

const Controls = styled(Box)(({ theme, $isVisible }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(2),
  background: "linear-gradient(transparent, rgba(0, 0, 0, 0.7))",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  opacity: $isVisible ? 1 : 0,
  transition: "opacity 0.3s",
  "&:hover": {
    opacity: 1,
  },
}));

const VideoReader = () => {
  const { id } = useParams();
  const { showSnackbar } = useSnackbar();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const videoRef = React.useRef(null);
  const controlsTimeoutRef = React.useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        setLoading(true);
        const response = await videosAPI.getById(id);
        setVideo(response.data);
      } catch (error) {
        showSnackbar(t("videos.messages.loadError"), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchVideo();
  }, [id, showSnackbar, t]);

  useEffect(() => {
    const hideControls = () => {
      if (playing) {
        setControlsVisible(false);
      }
    };

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    if (playing) {
      controlsTimeoutRef.current = setTimeout(hideControls, 3000);
    }

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [playing, controlsVisible]);

  const handleMouseMove = () => {
    setControlsVisible(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (playing) {
      controlsTimeoutRef.current = setTimeout(() => {
        setControlsVisible(false);
      }, 3000);
    }
  };

  const handlePlayPause = React.useCallback(() => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setPlaying(!playing);
    }
  }, [playing]);

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleVolumeChange = (event, newValue) => {
    if (videoRef.current) {
      videoRef.current.volume = newValue;
      setVolume(newValue);
      setMuted(newValue === 0);
    }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      setMuted(!muted);
    }
  };

  const handleSeek = (event, newValue) => {
    if (videoRef.current) {
      videoRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        videoRef.current.requestFullscreen();
      }
    }
  };

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleKeyPress = React.useCallback(
    (event) => {
      switch (event.code) {
        case "Space":
          event.preventDefault();
          handlePlayPause();
          break;
        case "ArrowLeft":
          if (videoRef.current) {
            videoRef.current.currentTime -= 5;
          }
          break;
        case "ArrowRight":
          if (videoRef.current) {
            videoRef.current.currentTime += 5;
          }
          break;
        case "KeyM":
          handleMuteToggle();
          break;
        case "KeyF":
          handleFullscreen();
          break;
        default:
          break;
      }
    },
    [handlePlayPause, handleMuteToggle, handleFullscreen]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  if (loading || !video) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <CustomContainer>
      <VideoContainer elevation={3} onMouseMove={handleMouseMove}>
        <VideoPlayer
          ref={videoRef}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setPlaying(false)}
          onClick={handlePlayPause}
          onError={() => showSnackbar(t("videos.messages.loadError"), "error")}
          controls={false}
        >
          <source src={video.url} type="video/mp4" />
          {t("videos.messages.browserNotSupported")}
        </VideoPlayer>

        <Controls $isVisible={controlsVisible}>
          <Stack direction="row" spacing={2} alignItems="center">
            <IconButton onClick={handlePlayPause} sx={{ color: "#fff" }}>
              {playing ? <Pause /> : <PlayArrow />}
            </IconButton>
            <Typography color="white" variant="body2">
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
            <Box sx={{ flexGrow: 1, mx: 2 }}>
              <Slider
                value={currentTime}
                max={duration}
                onChange={handleSeek}
                aria-label="time-indicator"
                size="small"
                sx={{
                  color: "#fff",
                  height: 4,
                  "& .MuiSlider-thumb": {
                    width: 8,
                    height: 8,
                    transition: "0.3s cubic-bezier(.47,1.64,.41,.8)",
                    "&:hover, &.Mui-focusVisible": {
                      boxShadow: "0 0 0 8px rgba(255, 255, 255, 0.16)",
                    },
                  },
                }}
              />
            </Box>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ minWidth: 150 }}
            >
              <IconButton onClick={handleMuteToggle} sx={{ color: "#fff" }}>
                {muted ? <VolumeOff /> : <VolumeUp />}
              </IconButton>
              <Slider
                value={muted ? 0 : volume}
                onChange={handleVolumeChange}
                aria-label="Volume"
                min={0}
                max={1}
                step={0.1}
                sx={{
                  color: "#fff",
                  "& .MuiSlider-track": {
                    border: "none",
                  },
                  "& .MuiSlider-thumb": {
                    width: 12,
                    height: 12,
                    backgroundColor: "#fff",
                    "&:hover, &.Mui-focusVisible": {
                      boxShadow: "0 0 0 8px rgba(255, 255, 255, 0.16)",
                    },
                  },
                }}
              />
            </Stack>
            <IconButton onClick={handleFullscreen} sx={{ color: "#fff" }}>
              <Fullscreen />
            </IconButton>
          </Stack>
        </Controls>
      </VideoContainer>

      <Paper sx={{ mt: 2, p: 2 }}>
        <Typography variant="h5" gutterBottom>
          {video.title}
        </Typography>
        {video.description && (
          <Typography variant="body1" color="text.secondary">
            {video.description}
          </Typography>
        )}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t("videos.details.uploadedBy")}: {video.user?.username}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            {t("videos.details.duration")}: {Math.floor(video.duration / 60)}:
            {String(video.duration % 60).padStart(2, "0")}{" "}
            {t("videos.details.minutes")}
          </Typography>
        </Box>
      </Paper>
    </CustomContainer>
  );
};

// Styled video component
const VideoPlayer = styled("video")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  backgroundColor: "#000",
});

export default VideoReader;
